import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserClock, FaCheck, FaTimes, FaSpinner, FaUser, FaEnvelope, FaPhone, FaUserTie } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingClientApprovals = () => {
  const [pendingClients, setPendingClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [message, setMessage] = useState(null);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchPendingClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-clients`, { headers: headers() });
      setPendingClients(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPendingClients(); }, []);

  const handleApprove = async (userId, clientName) => {
    if (!window.confirm(`Approve ${clientName}? They will get full access to all features.`)) return;
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/clients/${userId}/approve`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `${clientName} has been approved successfully.` });
      setPendingClients(prev => prev.filter(c => c.id !== userId));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to approve client' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, clientName) => {
    if (!rejectReason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason for rejection.' });
      return;
    }
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/clients/${userId}/reject`, { reason: rejectReason }, { headers: headers() });
      setMessage({ type: 'success', text: `${clientName} has been rejected.` });
      setPendingClients(prev => prev.filter(c => c.id !== userId));
      setRejectingId(null);
      setRejectReason('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reject client' });
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className="bg-[#0F172A] rounded-xl border border-[#1E3A5F] p-6">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0F172A] rounded-xl border border-[#1E3A5F] p-6" data-testid="pending-client-approvals">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <FaUserClock className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Pending Client Approvals</h3>
            <p className="text-gray-400 text-sm">{pendingClients.length} client{pendingClients.length !== 1 ? 's' : ''} awaiting approval</p>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`} data-testid="approval-message">
          {message.text}
        </div>
      )}

      {pendingClients.length === 0 ? (
        <div className="text-center py-8">
          <FaCheck className="text-green-400 text-3xl mx-auto mb-3" />
          <p className="text-gray-400">No pending client approvals</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingClients.map(client => (
            <div key={client.id} className="bg-[#1A2332] rounded-lg border border-[#2D5A8F]/50 p-4" data-testid={`pending-client-${client.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="flex items-center gap-2">
                    <FaUser className="text-gray-500 text-xs flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold text-sm">{client.name || 'N/A'}</p>
                      <p className="text-gray-500 text-xs">Client Name</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaEnvelope className="text-gray-500 text-xs flex-shrink-0" />
                    <div>
                      <p className="text-gray-300 text-sm">{client.email || 'N/A'}</p>
                      <p className="text-gray-500 text-xs">Email</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaPhone className="text-gray-500 text-xs flex-shrink-0" />
                    <div>
                      <p className="text-gray-300 text-sm">{client.phone || 'N/A'}</p>
                      <p className="text-gray-500 text-xs">Phone</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaUserTie className="text-gray-500 text-xs flex-shrink-0" />
                    <div>
                      <p className="text-blue-400 text-sm font-medium">{client.advisor_name || 'N/A'}</p>
                      <p className="text-gray-500 text-xs">Added by Advisor</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {rejectingId === client.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason for rejection..."
                        className="bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-1.5 text-white text-sm w-48 focus:outline-none focus:border-amber-500"
                        data-testid={`reject-reason-${client.id}`}
                      />
                      <button
                        onClick={() => handleReject(client.id, client.name)}
                        disabled={actionLoading === client.id}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                        data-testid={`confirm-reject-btn-${client.id}`}
                      >
                        {actionLoading === client.id ? <FaSpinner className="animate-spin" /> : 'Confirm'}
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectReason(''); }}
                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleApprove(client.id, client.name)}
                        disabled={actionLoading === client.id}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                        data-testid={`approve-btn-${client.id}`}
                      >
                        {actionLoading === client.id ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectingId(client.id)}
                        disabled={actionLoading === client.id}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors border border-red-500/30"
                        data-testid={`reject-btn-${client.id}`}
                      >
                        <FaTimes />
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-2 text-gray-500 text-xs">
                Created: {client.created_at ? new Date(client.created_at).toLocaleString('en-IN') : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingClientApprovals;
